import type { domain } from '@origo/seksjon';
import type { Område, OmrådeParentType } from '@origo/seksjon';

export type { Område, OmrådeKind, OmrådeParentType } from '@origo/seksjon';

export type SeksjonsType =
    | 'plan'
    | 'delomradetype'
    | 'delomradegruppe'
    | 'delomrade'
    | 'omrade'
    | 'kommune'
    | 'delomrade-admin'
    | 'egendefinerte-admin'
    | 'innstillinger';

export interface SelectedSectionContext {
    type: domain.SeksjonsType;
    PlanId: string;
    OmrådeId?: string;
    Delområdetype?: string;
    OmrådeNavn?: string;
    Delområde?: string;
}

/**
 * Admin-fasadens seksjonstype aliases `domain.AdminSeksjon` direkte. Tidligere
 * hadde admin en egen type (`skjult`, `id: string`) med adapter; den ble fjernet
 * i Fase 4.5. Komponentene bruker nå `objectId: number`, `synlig: boolean` osv.
 * direkte fra domenemodellen.
 */
export type Seksjon = domain.AdminSeksjon;

/**
 * Form-felter som editor-komponenten emiterer ved lagring. Kun de feltene
 * brukeren kan endre i skjemaet — system-/kontekst-felt settes av
 * `useAdminSections.addSeksjon` / `updateSeksjon`.
 */
export interface SeksjonFormValues {
    tittel: string;
    tekst: string;
    kommunal: boolean;
    topp?: boolean;
    infoboks?: boolean;
    synlig?: boolean;
}

export interface SeksjonContext {
    type: SeksjonsType;
    planId: string;
    omradeId?: string;
    omradeNavn?: string;
    delomradetype?: string;
    /** Bare for `delomradegruppe`-kontekst — Område.id for selve gruppen. */
    gruppeId?: string;
}

/** Stable string key that identifies a unique navigation context. */
export function contextId(ctx: SeksjonContext): string {
    if (ctx.type === 'plan') return 'plan';
    if (ctx.type === 'delomrade-admin') return 'da';
    if (ctx.type === 'egendefinerte-admin') return 'ea';
    if (ctx.type === 'delomradetype') return `dt-${ctx.delomradetype}`;
    if (ctx.type === 'delomradegruppe') return `dg-${ctx.gruppeId}`;
    if (ctx.type === 'delomrade') return ctx.omradeId ?? 'plan';
    return ctx.type;
}

/**
 * Narrow the unified Område type to "parent-eligible" metadata for form
 * parent-pickers. Consumers only need id/navn/kind to render options.
 */
export type ParentCandidate = Pick<Område, 'id' | 'navn' | 'kind'> & {
    parentType: OmrådeParentType;
};
